import { Injectable } from '@angular/core';
import { IItemForCalculatingRatioOption } from '@modeso/aumico-lib-annualreport-fe';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingAccountItem,
    AccountType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IDisplayOptions,
    IReclassesAccounts,
    IReclassesAndAdjustmentExtension,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { cloneDeep } from 'lodash-es';
import { IAccountWithRatio } from '../../../interfaces/IAccountWithRatio.interface';
import { IMappingAndReclassesAccounts } from '../../../interfaces/IMappingAndReclassesAccounts.interface';
import { IPrintPreviewMappingRatio } from '../../../interfaces/IPrintPreviewMappingRatio.interface';
import { IPrintPreviewMappingValue } from '../../../interfaces/IPrintPreviewMappingValue.interface';
import { IReferenceValueForRatioCalculation } from '../../../interfaces/IReferenceValueForRatioCalculation.interface';
import { isAccount } from '../../../shared/util/accountTypeGuard';

@Injectable({ providedIn: 'root' })
export class AccountMappingDisplayHelper {
    public getReClassItems(
        item:
            | IAccountMappingTemplateGroup
            | IAccountMappingTemplateGroupItemSubtotal
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingSubFLI,
    ): IReclassesAndAdjustmentExtension {
        const reclassItems: IReclassesAndAdjustmentExtension = {
            closingAdjustment: item.closingAdjustment || 0,
            openingAdjustment: item.openingAdjustment || 0,
            closingReClass: item.closingReClass || 0,
            openingReClass: item.openingReClass || 0,
        };
        return reclassItems;
    }

    public getOpeningAndClosingValues(
        item:
            | IAccountMappingTemplateGroup
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingSubFLI
            | IAccountMappingTemplateGroupItemSubtotal
            | IItemForCalculatingRatioOption,
        showReClassInSums: boolean,
        showAdjustmentInSums: boolean,
    ): IPrintPreviewMappingValue {
        // For SubFLIs/OF_THAT_ITEMs, reclasses and adjustments are already baked into
        // calculatedClosingBalance by applyPrintOptionsOnAccountsAndSubFlis, so don't add them again.
        const isSubFli =
            (item as IAccountMappingSubFLI).type === ItemType.SUB_FLI ||
            (item as IAccountMappingSubFLI).type === ItemType.OF_THAT_ITEM;

        let closingReclass: number | undefined;
        if (!isSubFli && showReClassInSums && item.closingReClass != null && NumberUtils.isFinite(item.closingReClass)) {
            closingReclass = NumberUtils.roundNumber(item.closingReClass);
        }

        let closingAdjustment: number | undefined;
        if (!isSubFli && showAdjustmentInSums && item.closingAdjustment != null && NumberUtils.isFinite(item.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(item.closingAdjustment);
        }

        let openingReclasses: number | undefined;
        if (!isSubFli && showReClassInSums && item.openingReClass != null && NumberUtils.isFinite(item.openingReClass)) {
            openingReclasses = NumberUtils.roundNumber(item.openingReClass);
        }

        let openingAdjustment: number | undefined;
        if (!isSubFli && showAdjustmentInSums && item.openingAdjustment != null && NumberUtils.isFinite(item.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(item.openingAdjustment);
        }

        const closingValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [item.calculatedClosingBalance, closingReclass, closingAdjustment],
            0,
        );

        const openingValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [item.calculatedOpeningBalance, openingReclasses, openingAdjustment],
            0,
        );

        return {
            closingValue: closingValue,
            openingValue: openingValue,
        };
    }

    public getOpeningAndClosingRatiosValues(
        itemOpeningValue: number,
        itemClosingValue: number,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation | undefined,
        hasNoPreviousYear: boolean,
    ): IPrintPreviewMappingRatio {
        const printPreviewMappingRatios: IPrintPreviewMappingRatio = {
            closingRatioValue: null,
            openingRatioValue: null,
        };

        if (referenceValuesForRatioCalculation == null) {
            return printPreviewMappingRatios;
        }

        printPreviewMappingRatios.closingRatioValue = this.getRatio(
            itemClosingValue,
            referenceValuesForRatioCalculation.closingReferenceValue,
        );

        if (hasNoPreviousYear) {
            return printPreviewMappingRatios;
        }

        printPreviewMappingRatios.openingRatioValue = this.getRatio(
            itemOpeningValue,
            referenceValuesForRatioCalculation.openingReferenceValue,
        );

        return printPreviewMappingRatios;
    }

    private getRatio(itemvalue: number, referenceValue: number): number | null {
        if (
            !NumberUtils.isFinite(referenceValue) ||
            NumberUtils.isZero(referenceValue) ||
            !NumberUtils.isFinite(itemvalue)
        ) {
            return null;
        }

        return NumberUtils.calculatePercentage(itemvalue, referenceValue);
    }

    public applyPrintOptionsOnAccounts(
        options: IDisplayOptions,
        accounts: IMappingAndReclassesAccounts,
    ): AccountMappingAccountItem[] {
        const mappedAccounts = cloneDeep<AccountMappingAccountItem[]>(accounts.mappedAccounts);

        let updatedAccounts = mappedAccounts.filter(
            (account) => account.type !== ItemType.SUB_FLI && account.type !== ItemType.OF_THAT_ITEM,
        ) as AccountMappingAccountItem[];

        if (options.showAdjustmentInSums || options.showReClassInSums) {
            updatedAccounts = this.getAdjustmentsAndReclassesOfAccounts(
                options.showAdjustmentInSums,
                options.showReClassInSums,
                { mappedAccounts: updatedAccounts, reclassesAccounts: accounts.reclassesAccounts },
            );
        }

        // Removing empty accounts should be done after adjustments & reclasses as they will affect the values
        if (options.hideEmptyLines || options.finStatementHideEmptyLines) {
            updatedAccounts = this.removeEmptyAccounts(updatedAccounts);
        }

        if (options.showShareholderInformation) {
            updatedAccounts = AccountMappingDisplayHelper.getAccountsWithShareholderName(updatedAccounts);
        } else {
            updatedAccounts = AccountMappingDisplayHelper.groupShareholderAccounts(updatedAccounts);
        }
        return updatedAccounts;
    }

    public applyPrintOptionsOnAccountsAndSubFlis(
        options: IDisplayOptions,
        accounts: IMappingAndReclassesAccounts,
    ): AccountMappingAccountItem[] {
        const mappedAccounts = cloneDeep<AccountMappingAccountItem[]>(accounts.mappedAccounts);

        let updatedAccounts = mappedAccounts;

        if (options.showAdjustmentInSums || options.showReClassInSums) {
            updatedAccounts = this.getAdjustmentsAndReclassesOfAccounts(
                options.showAdjustmentInSums,
                options.showReClassInSums,
                { mappedAccounts: updatedAccounts, reclassesAccounts: accounts.reclassesAccounts },
            );
        }

        // Removing empty accounts should be done after adjustments & reclasses as they will affect the values
        if (options.hideEmptyLines || options.finStatementHideEmptyLines) {
            updatedAccounts = this.removeEmptyAccounts(updatedAccounts);
        }

        if (options.showShareholderInformation) {
            updatedAccounts = AccountMappingDisplayHelper.getAccountsWithShareholderName(updatedAccounts);
        } else {
            updatedAccounts = AccountMappingDisplayHelper.groupShareholderAccounts(updatedAccounts);
        }
        return updatedAccounts;
    }

    private getAdjustmentsAndReclassesOfAccounts(
        applyAdjustments: boolean,
        applyReclasses: boolean,
        accounts: IMappingAndReclassesAccounts,
    ): IAccount[] {
        let updatedAccounts = cloneDeep<IAccount[]>(accounts.mappedAccounts);
        if (applyReclasses) {
            updatedAccounts = this.applyReclasses(updatedAccounts, accounts.reclassesAccounts);
        }

        if (applyAdjustments) {
            updatedAccounts = this.applyAdjustments(updatedAccounts);
        }
        return updatedAccounts;
    }

    private applyReclasses(mappedAccounts: IAccount[], reclassesAccounts: IReclassesAccounts[]): IAccount[] {
        const allAccounts = mappedAccounts.concat(this.mapReclassesAccountsToMappedAccounts(reclassesAccounts));
        return allAccounts.map((account) => {
            account.calculatedClosingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                [account.calculatedClosingBalance, account.closingReClass],
                undefined,
            );

            account.calculatedOpeningBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                [account.calculatedOpeningBalance, account.openingReClass],
                undefined,
            );

            return account;
        });
    }

    private applyAdjustments(mappedAccounts: IAccount[]): IAccount[] {
        return mappedAccounts.map((account) => {
            account.calculatedClosingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                [account.calculatedClosingBalance, account.closingAdjustment],
                undefined,
            );

            account.calculatedOpeningBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                [account.calculatedOpeningBalance, account.openingAdjustment],
                undefined,
            );

            return account;
        });
    }

    private mapReclassesAccountsToMappedAccounts(ReclassesAccounts: IReclassesAccounts[]): IAccount[] {
        return ReclassesAccounts.map((account) => {
            return {
                accountNumber: account.accountNumber,
                name: account.name,
                closingBalance: account.calculatedClosingBalance ?? 0,
                openingBalance: account.calculatedOpeningBalance ?? 0,
                calculatedClosingBalance: account.calculatedClosingBalance,
                calculatedOpeningBalance: account.calculatedOpeningBalance,
                type: account.type,
                closingAdjustment: account.closingAdjustment,
                openingAdjustment: account.openingAdjustment,
                closingReClass: account.closingReClass,
                openingReClass: account.openingReClass,
            };
        });
    }

    private removeEmptyAccounts(accounts: AccountMappingAccountItem[]): AccountMappingAccountItem[] {
        return accounts.filter((account) => {
            if (account.type === ItemType.HEADER || account.type === ItemType.EMPTY) return true;
            const isCloseValueNotEqualZero = !NumberUtils.isZero(account.calculatedClosingBalance ?? NaN);
            const isOpenValueNotEqualZero = !NumberUtils.isZero(account.calculatedOpeningBalance ?? NaN);
            const isAccountValuesAreNotEqualZeros = isCloseValueNotEqualZero || isOpenValueNotEqualZero;
            return isAccountValuesAreNotEqualZeros;
        });
    }

    private addingAccountNumbers(accounts: AccountMappingAccountItem[]): AccountMappingAccountItem[] {
        accounts.forEach((account) => {
            if (isAccount(account)) {
                if (account.type === AccountType.UNCOVERED_SHORTFALL || account.type === AccountType.RECLASSES) {
                    account.name = `${account.name}`;
                } else {
                    account.name = `${account.accountNumber} ${account.name}`;
                }
            }
        });
        return accounts;
    }

    public calculateAccountsRatios(
        accounts: AccountMappingAccountItem[],
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
    ): (IAccountWithRatio | AccountMappingAccountItem)[] {
        const accountsWithRatios: (IAccountWithRatio | AccountMappingAccountItem)[] = [];
        accounts.forEach((account) => {
            if (!isAccount(account)) {
                accountsWithRatios.push(account);
            } else {
                let accountOpeningRatioValue = 0;
                let accountClosingRatioValue = 0;
                if (
                    account.calculatedOpeningBalance != null &&
                    NumberUtils.isFinite(account.calculatedOpeningBalance)
                ) {
                    accountOpeningRatioValue = NumberUtils.calculatePercentage(
                        account.calculatedOpeningBalance,
                        referenceValuesForRatioCalculation.openingReferenceValue,
                    );
                }

                if (
                    account.calculatedClosingBalance != null &&
                    NumberUtils.isFinite(account.calculatedClosingBalance)
                ) {
                    accountClosingRatioValue = NumberUtils.calculatePercentage(
                        account.calculatedClosingBalance,
                        referenceValuesForRatioCalculation.closingReferenceValue,
                    );
                }
                const accountWithRatios = cloneDeep<IAccount>(account);
                accountsWithRatios.push({
                    ...accountWithRatios,
                    openingRatioValue: accountOpeningRatioValue,
                    closingRatioValue: accountClosingRatioValue,
                });
            }
        });
        return accountsWithRatios;
    }

    public static getAttachmentReferencesOfFli(
        fli: AccountMappingAccountItem | IAccountMappingTemplateGroupItemFLI,
        attachmentReferences: Map<string, string>,
    ): string {
        if (attachmentReferences == null) {
            return '';
        }
        const fliAttachmentReferences = attachmentReferences.get(fli.name);

        return fliAttachmentReferences != null ? fliAttachmentReferences : '';
    }

    static getAccountsWithShareholderName(
        fliPrintPreviewMappingAccounts: AccountMappingAccountItem[],
    ): AccountMappingAccountItem[] {
        return fliPrintPreviewMappingAccounts.map((account) => {
            if (isAccount(account)) {
                return {
                    ...account,
                    name:
                        account.shareholder != null && account.shareholder !== ''
                            ? account.name + ' - ' + account.shareholder
                            : account.name,
                };
            }
            return account;
        });
    }

    static groupShareholderAccounts(accounts: AccountMappingAccountItem[]): AccountMappingAccountItem[] {
        // JavaScript Maps preserve insertion order. 
        // We use this to keep the accounts in the order they were first encountered.
        const groupedAccounts: Map<string | number, AccountMappingAccountItem> = new Map();

        // We need a unique way to track ignored accounts so they don't overwrite each other in the Map
        let ignoredCounter = 0;

        for (const account of accounts) {
            if (account.type === ItemType.EMPTY || account.type === ItemType.HEADER || account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                groupedAccounts.set(`meta-${account.name}-${ignoredCounter++}`, account);
                continue;
            }

            if (isAccount(account)) {
                if (account.shareholder == null) {
                    const accountCopy: IAccount = cloneDeep<IAccount>(account);
                    accountCopy.shareholder = null;
                    // Add to Map immediately to preserve position, using a unique key
                    groupedAccounts.set(`ignored-${ignoredCounter++}`, accountCopy);
                    continue;
                }

                const accountNumber = account.accountNumber;

                if (groupedAccounts.has(accountNumber)) {
                    const groupedAccount = groupedAccounts.get(accountNumber) as IAccount;

                    groupedAccount.openingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                        [groupedAccount.openingBalance, account.openingBalance], 0
                    );
                    groupedAccount.closingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                        [groupedAccount.closingBalance, account.closingBalance], 0
                    );
                    groupedAccount.calculatedOpeningBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                        [groupedAccount.calculatedOpeningBalance, account.calculatedOpeningBalance], 0
                    );
                    groupedAccount.calculatedClosingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                        [groupedAccount.calculatedClosingBalance, account.calculatedClosingBalance], 0
                    );
                } else {
                    const accountCopy: IAccount = cloneDeep<IAccount>(account);
                    accountCopy.shareholder = null;
                    // First time seeing this account number, insert it now to lock its position
                    groupedAccounts.set(accountNumber, accountCopy);
                }
            }
        }

        return Array.from(groupedAccounts.values());
    }
}
